import * as Tone from 'tone';

/**
 * Local instrument sample library.
 *
 * Samples are vendored into public/samples/<folder>/ (originally from the
 * tonejs-instruments collection) so the game never depends on a third-party
 * CDN at runtime. If a sample file still fails to load — or hasn't finished
 * loading yet — a plain synth stands in so a track is never silent.
 */

export function sampleBaseUrl(folder) {
  const base = import.meta.env?.BASE_URL || '/';
  return `${base}samples/${folder}/`;
}

/**
 * A Tone.Sampler with a lazy PolySynth understudy. Exposes the same
 * surface the game uses (triggerAttackRelease / releaseAll / dispose),
 * routing to the sampler once its buffers are ready and to the synth
 * before that or after a load failure.
 */
class ResilientSampler {
  constructor(role, volumeDb, destination) {
    this._destination = destination;
    this._volumeDb = volumeDb;
    this._fallback = null;
    this._sampler = new Tone.Sampler({
      urls: role.sampleMapping,
      baseUrl: sampleBaseUrl(role.sampleFolder || role.id),
      volume: volumeDb,
      onerror: (err) => {
        console.warn(`[audio] sample load failed for "${role.id}", using synth fallback`, err);
      },
    }).connect(destination);
  }

  get loaded() {
    return this._sampler.loaded;
  }

  triggerAttackRelease(note, duration, time, velocity) {
    const target = this._sampler.loaded ? this._sampler : this._ensureFallback();
    return target.triggerAttackRelease(note, duration, time, velocity);
  }

  releaseAll(time) {
    try { this._sampler.releaseAll(time); } catch (_) {}
    try { this._fallback?.releaseAll(time); } catch (_) {}
  }

  _ensureFallback() {
    if (!this._fallback) {
      this._fallback = new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: 'triangle' },
        envelope: { attack: 0.02, decay: 0.2, sustain: 0.3, release: 0.4 },
        volume: this._volumeDb - 2,
      }).connect(this._destination);
    }
    return this._fallback;
  }

  dispose() {
    try { this._sampler.dispose(); } catch (_) {}
    try { this._fallback?.dispose(); } catch (_) {}
  }
}

/** Build the instrument for a role, wired into `destination`. */
export function createRoleSampler(role, volumeDb, destination) {
  return new ResilientSampler(role, volumeDb, destination);
}
